import React from 'react';
import { ArrowRight, Sparkles, Flame, Clock, Beef, Zap, CheckCheck } from 'lucide-react';

export default function Hero({ t }) {
  const h = t.hero;

  return (
    <section className="relative min-h-screen flex items-center bg-ink bg-grid-dark overflow-hidden pt-24 pb-16">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-volt-400/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-pulse-500/15 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-volt-400/5 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <div className="flex flex-col gap-7">
            <span className="inline-flex items-center gap-2 w-fit px-4 py-2 rounded-full border border-volt-400/30 bg-volt-400/10 text-volt-400 text-xs font-bold uppercase tracking-[0.18em]">
              <Sparkles size={14} />
              {h.badge}
            </span>

            <h1 className="font-display font-bold leading-[0.95] tracking-tight">
              <span className="block text-4xl sm:text-5xl lg:text-6xl text-frost">{h.line1}</span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl text-volt-400">{h.line2}</span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl text-outline">{h.line3}</span>
            </h1>

            <p className="text-lg text-mist max-w-xl leading-relaxed">{h.sub}</p>

            <p className="inline-flex items-center w-fit px-4 py-2 rounded-full bg-raised border border-line text-sm font-semibold text-frost">
              {h.price}
            </p>

            <div className="flex flex-col gap-3 max-w-md">
              <a
                href="/s"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-volt-400 text-ink font-bold text-base rounded-full shadow-volt-glow hover:bg-volt-300 hover:shadow-volt-glow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                {h.cta}
                <ArrowRight size={18} strokeWidth={2.5} />
              </a>
              <p className="text-xs text-mist leading-relaxed">
                {h.consent}{' '}
                <a href="/agb.html" className="text-volt-400 hover:underline">
                  AGB
                </a>
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-2 max-w-md">
              {h.stats.map((s) => (
                <div key={s.label} className="flex flex-col gap-1 border-l-2 border-volt-400/40 pl-3">
                  <span className="font-display text-2xl sm:text-3xl font-bold text-frost">{s.value}</span>
                  <span className="text-xs text-mist leading-snug">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative flex flex-col gap-6">
            <div className="relative">
              <div className="relative w-full rounded-[2rem] border border-line bg-abyss shadow-card p-3">
                <div className="rounded-[1.6rem] bg-surface overflow-hidden">
                  <div className="flex items-center gap-3 px-5 pt-5 pb-3 border-b border-line">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-volt-400 to-pulse-500 flex items-center justify-center">
                        <Zap size={18} className="text-ink" fill="currentColor" />
                      </div>
                      <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-volt-400 border-2 border-surface" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-frost">Fitupkeep Coach</p>
                      <p className="text-[11px] text-volt-400 font-medium">Lazar Angelov · Online</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 px-4 py-4">
                    {h.chat.map((m, i) => (
                      <div
                        key={i}
                        className={`max-w-[90%] px-4 py-3 text-[13px] leading-snug rounded-2xl ${
                          m.from === 'user'
                            ? 'self-end bg-pulse-500 text-white rounded-br-md'
                            : 'self-start bg-raised text-frost rounded-bl-md border border-line'
                        }`}
                      >
                        {m.text}
                        {m.from === 'ai' && (
                          <span className="flex justify-end mt-1">
                            <CheckCheck size={13} className="text-volt-400" />
                          </span>
                        )}
                      </div>
                    ))}

                    <div className="self-start w-[94%] rounded-2xl border border-volt-400/30 bg-ink/80 overflow-hidden">
                      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                        <Sparkles size={13} className="text-volt-400" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-volt-400">
                          {h.recipeCard.tag}
                        </span>
                      </div>
                      <p className="px-4 text-sm font-bold text-frost">{h.recipeCard.title}</p>
                      <div className="flex items-center gap-3 px-4 py-3 text-[11px] text-mist font-medium">
                        <span className="inline-flex items-center gap-1">
                          <Flame size={12} className="text-volt-400" /> {h.recipeCard.kcal}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Beef size={12} className="text-volt-400" /> {h.recipeCard.protein}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} className="text-volt-400" /> {h.recipeCard.time}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
